import {PercentPipe} from '@angular/common';
import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatTableDataSource} from '@angular/material/table';
import {OrderAdDetail} from '@core/models/order-ad-detail.model';
import {Order} from '@core/models/order.model';
import {AppConfigService} from '@core/services/app-config.service';
import {AppLocaleService} from '@core/services/app-locale.service';
import {OrderHelperService} from '@order/services/order-helper.service';
import {ProductPlacementSizeComboService} from '@order/services/product-placement-size-combo.service';
import {StaticDataCacheService} from '@order/services/static-data-cache.service';
import {OrderLineSummaryViewModel} from '@order/viewModels/order-line-summary.viewModel';
import {CassCurrencyPipe} from '@shared/directives/cass-currency.pipe';
import {MatButtonToggleChange} from '@angular/material/button-toggle';
import {PriceSummaryGroup, PriceSummaryLocalStorageService} from '@core/services/price-summary-local-storage.service';
import {UserAlertService} from '@shared/services/user-alert.service';

interface TableColumnDef {
	key: string;
	heading: string;
	value?: any;
	isGroupingColumn?: boolean;
	class?: string;
}

@Component({
	selector: 'app-publisher-price-summary-dialog',
	templateUrl: './publisher-price-summary-dialog.component.html',
	styleUrls: ['./publisher-price-summary-dialog.component.css']
})
export class PublisherPriceSummaryDialogComponent implements OnInit, OnDestroy {

	dataSource!: MatTableDataSource<any>;

	currencyPipe: CassCurrencyPipe;
	percentPipe: PercentPipe;

	orderLineSummariesVM: OrderLineSummaryViewModel [] = [];

	orderLinesTableColumnDefs: TableColumnDef[] = [
		{
			key: 'publisher',
			heading: 'ORDER_BASKET.ORDER_LINE_TABLE_COLUMNS.PUBLISHER',
			value: (data: any): string => data.publisher ?? '',
			isGroupingColumn: true,
		},
		{
			key: 'product',
			heading: 'ORDER_BASKET.ORDER_LINE_TABLE_COLUMNS.PRODUCT',
			value: (data: any): string => data.product ?? '',
			isGroupingColumn: true,
		},
		{
			key: 'size',
			heading: 'ORDER_BASKET.ORDER_LINE_TABLE_COLUMNS.SIZE',
			value: (data: any): string => data.size,
			isGroupingColumn: true,
		},
		{
			key: 'placement',
			heading: 'ORDER_BASKET.ORDER_LINE_TABLE_COLUMNS.PLACEMENT',
			value: (data: any): string => data.placement ?? '',
			isGroupingColumn: true,
		},
		{
			key: 'confirmationNumber',
			heading: 'ORDER_BASKET.ORDER_LINE_TABLE_COLUMNS.CONFIRMATION_NUM',
			value: (data: any): string => data.confirmationNumber ?? '',
			isGroupingColumn: true,
		},
		{
			key: 'month',
			heading: 'ORDER_BASKET.ORDER_LINE_TABLE_COLUMNS.MONTH',
			value: (data: any): string => data.month ?? '',
			isGroupingColumn: true,
		},
		{
			key: 'grossPrice',
			heading: 'ORDER_BASKET.ORDER_LINE_TABLE_COLUMNS.GROSS_PRICE',
			value: (data: any): string => this.currencyPipe.transform(data.priceGross, this.order.currencyCode) ?? '',
			class: 'align-right',
		},
		{
			key: 'discounts',
			heading: 'ORDER_BASKET.ORDER_LINE_TABLE_COLUMNS.DISCOUNTS',
			value: (data: any): string => this.currencyPipe.transform(data.discountAmount, this.order.currencyCode) ?? '',
			class: 'align-right',
		},
		{
			key: 'netPriceExComm',
			heading: 'ORDER_BASKET.ORDER_LINE_TABLE_COLUMNS.NET_EX_COMMISSION',
			value: (data: any): string => this.currencyPipe.transform(data.netPriceExComm, this.order.currencyCode) ?? '',
			class: 'align-right',
		},
		{
			key: 'commission',
			heading: 'ORDER_BASKET.ORDER_LINE_TABLE_COLUMNS.COMMISSION',
			value: (data: any): string => this.currencyPipe.transform(data.commissionAmount, this.order.currencyCode) ?? '',
			class: 'align-right',
		},
		{
			key: 'netPrice',
			heading: 'ORDER_BASKET.ORDER_LINE_TABLE_COLUMNS.NET_PRICE',
			value: (data: any): string => this.currencyPipe.transform(data.priceNet, this.order.currencyCode) ?? '',
			class: 'align-right',
		},
	];

	private initialOrderLinesTableDisplayedColumns: TableColumnDef[] = [];
	orderLinesTableDisplayedColumns: string[] = [];

	primaryGroup: string | string[] = ['publisher', 'product',];
	secondaryGroup: string | undefined;

	persistentGroupSnapshot: PriceSummaryGroup | undefined;

	constructor(
		@Inject(MAT_DIALOG_DATA) private order: Order,
		private dialogRef: MatDialogRef<PublisherPriceSummaryDialogComponent>,
		private _staticDataCacheService: StaticDataCacheService,
		private _productPlacementSizeComboService: ProductPlacementSizeComboService,
		private _orderHelperService: OrderHelperService,
		private _appConfigService: AppConfigService,
		private _priceSummaryLocalStorageService: PriceSummaryLocalStorageService,
		private _alertService: UserAlertService,
		public localeService: AppLocaleService,
	) {
		this.currencyPipe = new CassCurrencyPipe(localeService, this._appConfigService);
		this.percentPipe = new PercentPipe(localeService.currentOrDefaultLocaleId);
	}

	ngOnInit(): void {

		// Get Persistent Grouping from localStorage if orderId is not undefined
		const orderId = this.order.orderHeader.id;
		if (orderId) {
			this.persistentGroupSnapshot = this._priceSummaryLocalStorageService.loadGroup(orderId);
			if (this.persistentGroupSnapshot) {
				this.primaryGroup = this.persistentGroupSnapshot.level1;
				this.secondaryGroup = this.persistentGroupSnapshot.level2;
			}
		}

		// Initialize table data and sort the data by Publisher, Product, Size & Placement
		this.initTableData(this.order.orderAdDetails);
		// Generate Grouped table based on the grouping selected by User
		this.dataSource = new MatTableDataSource(this.generateTable(this.orderLineSummariesVM));

		const isDirectOrder = this._orderHelperService.getIsDirectOrder(this.order);

		this.initialOrderLinesTableDisplayedColumns = [...this.orderLinesTableColumnDefs.filter((x) => {
			return !(isDirectOrder && ['netPriceExComm', 'commission'].includes(x.key)); // exclude "netPriceExComm" and "commission" columns when isDirectOrder is true
		})];

		this.orderLinesTableDisplayedColumns = [...this.initialOrderLinesTableDisplayedColumns.map(x => x.key)];

		// Only show column for whose grouping is selected by user
		this.updateDisplayedColumns();
	}

	private initTableData(orderAdDetails: OrderAdDetail[]) {
		if (orderAdDetails && orderAdDetails.length > 0) {
			this.orderLineSummariesVM = orderAdDetails.map((detail) => {
				const orderAdDetailVM: OrderLineSummaryViewModel = {} as OrderLineSummaryViewModel;
				Object.assign(orderAdDetailVM, detail);
				orderAdDetailVM.publisher = this._staticDataCacheService.getProductById(detail.productId)?.company?.label ?? '';
				orderAdDetailVM.product = this._staticDataCacheService.getProductById(detail.productId)?.label ?? '';
				orderAdDetailVM.size = this.getSizeColumnData(detail);
				orderAdDetailVM.placement = this._staticDataCacheService.getPlacement(detail.packageId, detail.placementId)?.label ?? '';
				orderAdDetailVM.confirmationNumber = detail.confirmationNumber;
				orderAdDetailVM.month = new Date(detail.issueDate).toLocaleString(this.localeService.currentOrDefaultLocaleId, {month: 'short'});
				orderAdDetailVM.netPriceExComm = detail.priceGross - detail.discountAmount;
				orderAdDetailVM.priceNet = detail.priceGross - detail.discountAmount - detail.commissionAmount;
				return orderAdDetailVM;
			}).sort((a, b) => {
				const cPublisher = a.publisher.localeCompare(b.publisher);
				const cProduct = a.product.localeCompare(b.product);
				const cSize = a.size.localeCompare(b.size);
				const cPlacement = a.placement.localeCompare(b.placement);
				return cPublisher || cProduct || cSize || cPlacement;
			});
		}
	}

	closeDialog() {
		this.dialogRef.close();
	}

	private getSizeColumnData(data: OrderAdDetail): string {
		const size = this._productPlacementSizeComboService.getFixedSizeByDimensions(data.packageId, data.placementId, data.moduleCode, data.columns, data.depth);
		if (size) {
			return `${size.label}\n${data.width}x${data.depth}`;
		} else {
			return `${data.width}x${data.depth}`;
		}
	}

	onPrimaryGroupingChange(event: MatButtonToggleChange) {
		// If User unselect all the level 1 groups alert them with default grouping
		if (event.value && event.value.length === 0) {
			this._alertService.messageByTranslateKey('PRICE_SUMMARY_DIALOG.WARNING_MESSAGES.ONE_LEVEL_OF_GROUPING_REQUIRED');
			this.primaryGroup = ['publisher', 'product'];
		} else {
			this.primaryGroup = event.value;
		}
		// Remove columns which are not grouped
		this.updateDisplayedColumns();
		this.dataSource = new MatTableDataSource(this.generateTable(this.orderLineSummariesVM));
	}

	onSecondaryGroupingChange(event: MatButtonToggleChange) {
		this.secondaryGroup = event.value;

		// show column if selected by User in either grouping
		if (!this.primaryGroup.includes(event.value)) {
			this.primaryGroup = [...this.primaryGroup, event.value];
			this.updateDisplayedColumns();
		}
		// Recalculate the table
		this.dataSource = new MatTableDataSource(this.generateTable(this.orderLineSummariesVM));
	}

	clearAllSecondaryGrouping() {
		this.secondaryGroup = undefined;
		this.generateTable(this.orderLineSummariesVM);
	}

	updateDisplayedColumns() {
		this.orderLinesTableDisplayedColumns = this.initialOrderLinesTableDisplayedColumns
			.filter(col => !col.isGroupingColumn || this.primaryGroup.includes(col.key))
			.map(x => x.key);
	}

	saveGroupingState() {
		if (this.order.orderHeader.id) {
			this._priceSummaryLocalStorageService.saveSnapshot({
				orderId: this.order.orderHeader.id,
				level1: this.primaryGroup,
				level2: this.secondaryGroup
			});
		}
	}

	private generateTable = (data: OrderLineSummaryViewModel[]): OrderLineSummaryViewModel[] => {
		const moneyColumns = ['priceGross', 'priceNet', 'netPriceExComm', 'discountAmount', 'commissionAmount'];

		// Group data by primary group and sum money columns
		const summedData = data.reduce((acc: Record<string, any>, entry: any) => {
			const key = this.getLevelOneKey(entry);
			acc[key] = acc[key] || this.initializeSummedEntry(entry, moneyColumns);
			this.sumMoneyColumns(acc[key], entry, moneyColumns);
			return acc;
		}, {});

		// Convert summedData to an array
		let table: OrderLineSummaryViewModel[] = Object.values(summedData);

		// Group by secondary group if provided
		if (this.secondaryGroup) {
			table = this.groupByLevelTwo(table, moneyColumns) || [];
		}

		// Save grouping state in localStorage
		this.saveGroupingState();
		return table;
	};

	private getLevelOneKey = (entry: any): string => {
		return Array.isArray(this.primaryGroup)
			? this.primaryGroup.map((x) => entry[x]).join('_')
			: entry[this.primaryGroup];
	};

	private initializeSummedEntry = (entry: any, moneyColumns: string[]): any => {
		const summed: any = {};
		moneyColumns.forEach((column) => (summed[column] = 0));
		if (Array.isArray(this.primaryGroup)) {
			this.primaryGroup.forEach((x) => (summed[x] = entry[x]));
		} else {
			summed[this.primaryGroup] = entry[this.primaryGroup];
		}
		return summed;
	};

	private sumMoneyColumns = (summed: any, entry: any, moneyColumns: string[]): void => {
		moneyColumns.forEach((column) => {
			summed[column] += entry[column];
		});
	};

	private groupByLevelTwo = (table: OrderLineSummaryViewModel[], moneyColumns: string[]): OrderLineSummaryViewModel[] | undefined => {
		const secondaryGroupKey = this.secondaryGroup;
		if (!secondaryGroupKey) return;

		const groupedBySecondary: Record<string, any[]> = table.reduce((acc: Record<string, any[]>, entry: any) => {
			const key = entry[secondaryGroupKey];
			acc[key] = acc[key] || [];
			acc[key].push(entry);
			return acc;
		}, {});

		const groupedTable: OrderLineSummaryViewModel[] = [];
		Object.entries(groupedBySecondary).forEach(([key, group]) => {
			const summedEntry = this.sumGroupEntries(group, moneyColumns);
			summedEntry[secondaryGroupKey] = key !== 'undefined' && key ? key : 'N/A';
			groupedTable.push(...group, summedEntry);
		});

		return groupedTable;
	};

	private sumGroupEntries = (group: any[], moneyColumns: string[]): any => {
		const summed: any = {
			publisher: '',
			product: '',
			size: '',
			placement: '',
			confirmationNumber: '',
			month: '',
			isAggregateRow: true,
		};
		moneyColumns.forEach((column) => {
			summed[column] = group.reduce((acc, entry) => acc + entry[column], 0);
		});
		return summed;
	};

	ngOnDestroy(): void {
		this.saveGroupingState()
	}
}
